import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Layers, X, Loader2, Plus, ArrowUp, ArrowDown, Trash2, Pencil } from "lucide-react";
import aapi, { inr, formatApiError } from "./aapi";
import { PageHeader, EmptyState, inputCls, selectCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const PLATFORMS = ["Instagram", "YouTube", "Facebook", "TikTok", "Telegram", "X", "Other"];
const EMPTY_FORM = { platform: "Instagram", category: "", name: "", description: "", rate: "", min: "", max: "", eta: "", start_info: "", active: true };

export default function AdminServices() {
  const [services, setServices] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | sid
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = () => {
    aapi.get("/services").then(({ data }) => setServices(data)).catch(() => setServices([]));
    aapi.get("/categories").then(({ data }) => setCategories(data.filter((c) => c.enabled))).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...EMPTY_FORM, category: categories[0]?.name || "Followers" });
    setEditing("new");
  };

  const openEdit = (s) => {
    setForm({ platform: s.platform, category: s.category, name: s.name, description: s.description,
      rate: s.rate, min: s.min, max: s.max, eta: s.eta, start_info: s.start_info, active: s.active });
    setEditing(s.sid);
  };

  const save = async () => {
    setBusy(true);
    const payload = { ...form, rate: parseFloat(form.rate), min: parseInt(form.min, 10), max: parseInt(form.max, 10) };
    try {
      if (editing === "new") {
        await aapi.post("/services", payload);
        toast.success("Service created — live on the customer panel now");
      } else {
        await aapi.put(`/services/${editing}`, payload);
        toast.success("Service updated — customer panel reflects it instantly");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    await aapi.put(`/services/${s.sid}`, { active: !s.active }).then(() => {
      toast.success(`${s.sid} ${s.active ? "disabled" : "enabled"}`);
      load();
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete service #${s.sid} "${s.name}" permanently? This is audit-logged.`)) return;
    await aapi.delete(`/services/${s.sid}`).then(() => {
      toast.success("Service deleted");
      load();
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const move = async (idx, dir) => {
    const arr = [...services];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setServices(arr);
    await aapi.post("/services/reorder", { sids: arr.map((s) => s.sid) }).catch(() => {});
  };

  return (
    <div data-testid="admin-services-page">
      <PageHeader title="Service Management" sub="Edit here — the customer panel updates immediately.">
        <button onClick={openNew} data-testid="admin-add-service-button"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-5 py-2.5 text-xs font-bold text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.35)]">
          <Plus size={14} /> Add Service
        </button>
      </PageHeader>

      {services === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : services.length === 0 ? (
        <EmptyState icon={Layers} title="No services" sub="Add your first service." testid="admin-services-empty" />
      ) : (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-services-table">
          <div className="min-w-[980px]">
            <div className="grid grid-cols-12 gap-3 border-b border-white/10 bg-white/[0.02] px-5 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">
              <span className="col-span-1">Order</span>
              <span className="col-span-1">ID</span>
              <span className="col-span-3">Service</span>
              <span className="col-span-1">Platform</span>
              <span className="col-span-1">Category</span>
              <span className="col-span-1">Rate/1K</span>
              <span className="col-span-1">Min–Max</span>
              <span className="col-span-1">Active</span>
              <span className="col-span-2 text-right">Actions</span>
            </div>
            {services.map((s, i) => (
              <div key={s.sid} data-testid={`admin-service-row-${s.sid}`}
                className={`grid grid-cols-12 items-center gap-3 border-b border-white/5 px-5 py-3 transition-colors duration-300 last:border-0 hover:bg-gold/[0.04] ${s.active ? "bg-panel/80" : "bg-panel/40 opacity-60"}`}>
                <span className="col-span-1 flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`service-up-${s.sid}`} aria-label="Move up"
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 text-neutral-500 hover:border-gold/50 hover:text-goldlight disabled:opacity-30">
                    <ArrowUp size={12} />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === services.length - 1} data-testid={`service-down-${s.sid}`} aria-label="Move down"
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 text-neutral-500 hover:border-gold/50 hover:text-goldlight disabled:opacity-30">
                    <ArrowDown size={12} />
                  </button>
                </span>
                <span className="col-span-1 font-mono text-xs text-gold/80">#{s.sid}</span>
                <span className="col-span-3 truncate text-sm text-neutral-200" title={s.name}>{s.name}</span>
                <span className="col-span-1 text-xs text-neutral-400">{s.platform}</span>
                <span className="col-span-1 text-xs text-neutral-400">{s.category}</span>
                <span className="col-span-1 text-sm font-medium text-goldlight">{inr(s.rate)}</span>
                <span className="col-span-1 font-mono text-[10px] text-neutral-500">{s.min.toLocaleString("en-IN")}–{s.max.toLocaleString("en-IN")}</span>
                <span className="col-span-1">
                  <button onClick={() => toggleActive(s)} data-testid={`service-toggle-${s.sid}`} aria-label="Toggle active"
                    className={`relative h-6 w-11 rounded-full transition-colors duration-300 ${s.active ? "bg-gold" : "bg-white/10"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all duration-300 ${s.active ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </span>
                <span className="col-span-2 flex justify-end gap-2">
                  <button onClick={() => openEdit(s)} data-testid={`service-edit-${s.sid}`} aria-label="Edit"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gold/30 text-goldlight transition-colors hover:bg-gold hover:text-black">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(s)} data-testid={`service-delete-${s.sid}`} aria-label="Delete"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-400/30 text-rose-300 transition-colors hover:bg-rose-400/10">
                    <Trash2 size={13} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setEditing(null)}>
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }} transition={{ duration: 0.5, ease: EASE }}
              className="my-8 w-full max-w-2xl rounded-3xl border border-gold/20 bg-panel p-8"
              onClick={(e) => e.stopPropagation()} data-testid="admin-service-modal">
              <div className="flex items-start justify-between">
                <h3 className="text-xl font-bold text-neutral-50">{editing === "new" ? "Add Service" : `Edit Service #${editing}`}</h3>
                <button onClick={() => setEditing(null)} aria-label="Close" data-testid="admin-service-modal-close"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-neutral-400 hover:border-gold/50 hover:text-neutral-100">
                  <X size={16} />
                </button>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Platform</label>
                  <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className={`${selectCls} mt-2`} data-testid="service-form-platform">
                    {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${selectCls} mt-2`} data-testid="service-form-category">
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Service name</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} mt-2`} data-testid="service-form-name" />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Description</label>
                  <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} mt-2 resize-none`} data-testid="service-form-description" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Rate per 1000 (₹)</label>
                  <input type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className={`${inputCls} mt-2`} data-testid="service-form-rate" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Min</label>
                    <input type="number" value={form.min} onChange={(e) => setForm({ ...form, min: e.target.value })} className={`${inputCls} mt-2`} data-testid="service-form-min" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Max</label>
                    <input type="number" value={form.max} onChange={(e) => setForm({ ...form, max: e.target.value })} className={`${inputCls} mt-2`} data-testid="service-form-max" />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Processing info</label>
                  <input value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} placeholder="Starts 0–1 hr · 5K/day" className={`${inputCls} mt-2`} data-testid="service-form-eta" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Start info</label>
                  <input value={form.start_info} onChange={(e) => setForm({ ...form, start_info: e.target.value })} placeholder="Count captured at start" className={`${inputCls} mt-2`} data-testid="service-form-start" />
                </div>
              </div>
              <button onClick={save} disabled={busy || !form.name || !form.rate || !form.min || !form.max}
                data-testid="service-form-save"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-50">
                {busy && <Loader2 size={15} className="animate-spin" />}
                {editing === "new" ? "CREATE SERVICE" : "SAVE CHANGES"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
